// SEE-876 mileage-reimbursement math. Kept integer-only so it exactly matches
// the server's Decimal ROUND_HALF_UP check (v2-paidol/domains/mileage.py):
// miles and rate each carry at most two decimals, so scaling both to whole
// units makes the product exact and the .5 boundary lands on an exactly
// representable value (Math.round rounds half up for positives).

/** Total reimbursement in whole cents for `miles × rate`, round-half-up. */
export const computeMileageAmountCents = (miles: number, rate: number): number => {
  if (!Number.isFinite(miles) || !Number.isFinite(rate) || miles <= 0 || rate <= 0) {
    return 0;
  }
  const milesHundredths = Math.round(miles * 100);
  const rateHundredths = Math.round(rate * 100);
  return Math.round((milesHundredths * rateHundredths) / 100);
};

/** Total as a plain dollar string (e.g. "9.28") for the amount field / display. */
export const formatMileageAmountDollars = (miles: number, rate: number): string =>
  (computeMileageAmountCents(miles, rate) / 100).toFixed(2);

/** Non-negative number with up to two decimal places. */
export const isValidMileage = (raw: string | null | undefined): boolean => {
  if (!raw) return true; // empty = standard (non-mileage) reimbursement
  const trimmed = String(raw).trim();
  return /^\d+(\.\d{1,2})?$/.test(trimmed) && Number(trimmed) >= 0;
};

/**
 * Whether a raw input string is an acceptable *intermediate* value for the
 * miles-driven field — digits with an optional decimal point and at most two
 * decimals (so "13.", "13.2", "13.25" are allowed while "13.255" and "-1" are
 * not). Used to block keystrokes past the second decimal instead of erroring.
 */
export const isAllowedMileageInput = (raw: string): boolean => /^\d*(\.\d{0,2})?$/.test(raw);
